/* covered.ro — Stats band: count-up when the board scrolls into view.
   The final values are already in the HTML; without JS or with reduced
   motion nothing changes. */
(() => {
  if (customElements.get('cv-stats-counter')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const easeOutExpo = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
  const STAGGER = 120;

  // Romanian number format: thousands "." and decimal ",".
  const format = (value, decimals, thousands) => {
    const [int, frac] = Math.abs(value).toFixed(decimals).split('.');
    const grouped = thousands ? int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : int;
    return frac ? `${grouped},${frac}` : grouped;
  };

  class CvStatsCounter extends HTMLElement {
    connectedCallback() {
      this.counters = Array.from(this.querySelectorAll('[data-count-to]')).map((el) => ({
        el,
        item: el.closest('.cv-stats__item') || el,
        to: parseFloat(el.dataset.countTo) || 0,
        decimals: parseInt(el.dataset.decimals, 10) || 0,
        thousands: el.dataset.thousands !== 'false',
        final: el.dataset.final || el.textContent,
      }));

      if (
        !this.counters.length ||
        reduceMotion.matches ||
        document.body.classList.contains('disable-animations') ||
        !('IntersectionObserver' in window)
      ) {
        return;
      }

      this.duration = parseInt(this.dataset.duration, 10) || 1600;
      this.style.setProperty('--scan-duration', `${this.duration + STAGGER * (this.counters.length - 1)}ms`);
      this.render(0);

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.play();
        },
        { threshold: 0.35 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      cancelAnimationFrame(this.frame);
      // Leave real values behind if the element is moved or re-rendered.
      if (this.counters) this.finish();
    }

    render(elapsed) {
      let running = false;
      this.counters.forEach((counter, index) => {
        const t = Math.max(0, Math.min(1, (elapsed - index * STAGGER) / this.duration));
        const eased = easeOutExpo(t);
        if (t < 1) running = true;
        counter.el.textContent = t >= 1 ? counter.final : format(counter.to * eased, counter.decimals, counter.thousands);
        counter.item.style.setProperty('--p', eased.toFixed(4));
      });
      return running;
    }

    play() {
      this.classList.add('is-counting');
      const start = performance.now();
      const step = (now) => {
        if (this.render(now - start)) {
          this.frame = requestAnimationFrame(step);
        } else {
          this.finish();
        }
      };
      this.frame = requestAnimationFrame(step);
    }

    finish() {
      this.counters.forEach((counter) => {
        counter.el.textContent = counter.final;
        counter.item.style.removeProperty('--p');
      });
      this.classList.add('is-done');
    }
  }

  customElements.define('cv-stats-counter', CvStatsCounter);
})();
